/**
 * Where the refresh token lives.
 *
 * On web and Tauri desktop: localStorage, exactly as before — this module
 * changes nothing there.
 *
 * On a Capacitor native build: the iOS Keychain / Android Keystore, via
 * `@aparajita/capacitor-secure-storage`. The app runs from a persistent bundled
 * origin so it can cold-start offline (see capacitor.config.ts), which means
 * WebView localStorage survives a force-quit — a 7-day, indefinitely-rotating
 * refresh token sitting there is readable by anything that can read the app's
 * container. OWASP MASVS-AUTH is explicit that a local-authentication gate is
 * not a security control while the credential it guards is in plain storage, so
 * the biometric app lock and this move ship together.
 *
 * The ACCESS token deliberately stays in localStorage on every platform: it
 * lives 15 minutes and is read from roughly fifteen synchronous call sites,
 * one of them inside a component render body. Moving it is a much larger change
 * for a much smaller gain.
 *
 * This module is a **leaf** by the same contract as `storageKeys.ts` — it
 * imports only the keys, the platform flag, and (lazily, native-only) the
 * plugin. `axiosInstance` and `AuthContext` both depend on it, so anything more
 * would close an import cycle.
 */
import { isCapacitor } from '../api/config';
import { AUTH_STORAGE_KEYS } from './storageKeys';
import {
  classifyStoreFailure,
  clearStoredBindingMode,
  readStoredBindingMode,
  shouldBindRefreshToken,
  writeStoredBindingMode,
} from './tokenBinding';
import {
  armBiometrySetGuard,
  checkBiometrySetGuard,
  disarmBiometrySetGuard
} from './biometrySetGuard';

/**
 * The secure store could not be reached — the plugin is missing, the Keychain
 * is locked, the OS refused.
 *
 * Deliberately distinct from "there is no token". A caller that confuses the
 * two ends the session on a transient device fault, which on a cold start with
 * no network is precisely the offline case the auth bootstrap exists to
 * survive.
 */
export class SecureTokenStoreError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'SecureTokenStoreError';
  }
}

/** True when the refresh token is held by the OS keystore rather than localStorage. */
export function isSecureTokenStoreActive(): boolean {
  return isCapacitor;
}

/**
 * Synchronous "this session is over" latch.
 *
 * `handleSessionExpired()` in the axios interceptors is synchronous by
 * contract, and several sign-out paths are sync click handlers that navigate or
 * reload immediately afterwards. None of them can await a Keychain delete. So
 * `clearRefreshToken()` raises this flag first and fires the delete after: from
 * that instant every read answers null, whatever the plugin is still doing.
 * Without it a signed-out device could hand the next reader a token that is
 * moments from deletion — or, if the reload won the race, keep one forever.
 *
 * NATIVE ONLY, deliberately. On web and desktop the delete is a synchronous
 * `localStorage.removeItem`, so there is no window to cover — and a latch there
 * would make this module disagree with localStorage for any caller that writes
 * the key directly, which is exactly what the existing auth tests do.
 */
let clearedLatch = false;

/**
 * The one-time localStorage -> keystore migration, memoised as a PROMISE.
 *
 * A boolean flag raised before the write would be a race: a second concurrent
 * caller would see "already migrated", skip the wait, and read the keystore
 * before the first caller's write had landed — getting null for a token that
 * genuinely exists. `tokenRefresh` reports a null as `rejected`, which ends the
 * session, so the loser of that race is signed out. And it is reachable on
 * exactly the launch this ships in: with no cached policy the lock fails open,
 * so the auth bootstrap and a request interceptor both reach the store on the
 * same tick.
 */
let migrationPromise: Promise<void> | null = null;

/**
 * Every keystore operation runs in submission order on this chain.
 *
 * Without it a `clearRefreshToken()` whose delete is still in flight can resolve
 * AFTER a subsequent `setRefreshToken()` and delete the token that just replaced
 * it — sign out, sign straight back in on a shared device, and the new session
 * is silently unauthenticated on next launch. Serialising is cheap here: these
 * are three calls per session, not a hot path.
 */
let opChain: Promise<unknown> = Promise.resolve();

/**
 * The bound token, held for as long as the app stays unlocked.
 *
 * A biometry-bound keystore item cannot be read without a live authentication,
 * and `getRefreshToken()` is called by the axios request interceptor and the
 * auth bootstrap — neither of which can prompt, and both of which run on paths
 * where a prompt would be absurd. So in bound mode the keystore is read exactly
 * ONCE per unlock, by `unlockRefreshToken()`, and every reader afterwards is
 * served from here. That is what keeps silent refresh silent.
 *
 * Dropped by `lockRefreshToken()` when the lock ENGAGES — not when the app
 * merely backgrounds. Backgrounding below the idle threshold does not lock, and
 * clearing the hold there would leave a foregrounded, unlocked app with no token
 * and no unlock event coming to restore it.
 *
 * Always null in plain mode, where the keystore is readable directly.
 */
let sessionToken: string | null = null;

/**
 * Bumped by every lock and every sign-out.
 *
 * The hold is written from inside `opChain`-ordered callbacks — after an await
 * on the keystore — but it is CLEARED synchronously, because the paths that clear
 * it (a lock engaging, a sign-out handler that is about to navigate) cannot wait
 * for a queue. Those two mechanisms are not ordered against each other, so
 * without this a rotation still in flight when the lock engaged would resolve
 * afterwards and write the token straight back: the app would sit behind a lock
 * screen with a live, spendable refresh token, and the axios interceptor of any
 * component still mounted underneath would happily spend it.
 *
 * So every asynchronous writer captures the epoch before it starts and declines
 * to write if it has moved. A stale completion cannot undo a lock that happened
 * while it was in the air.
 */
let holdEpoch = 0;

/**
 * How long ONE keystore operation may stay in flight before it is abandoned.
 *
 * Every route to the OS keystore runs through `enqueue`, so this is the single
 * place that can promise the rest of the app a keystore call always answers.
 * Without it there are two failure modes, and the second is the one that hurts:
 *
 * 1. the caller waits forever — `setRefreshToken` sits inside `writeSession`,
 *    ahead of the `setState` that ends a login, so a wedged write is a login
 *    that never completes; and
 * 2. **every later operation waits forever too.** The chain only advances when
 *    the operation at its head settles, so one call that never comes back
 *    retires the keystore for the life of the process. The auth bootstrap's own
 *    `KEYSTORE_READ_TIMEOUT_MS` cannot help there: it abandons the bootstrap's
 *    WAIT, showing the login page, while the read it gave up on stays at the
 *    head of this queue — and the `setRefreshToken` that login then performs is
 *    queued behind it. That is the iOS report this constant closes: an app that
 *    reaches the login page, takes the credentials, and never signs in. No
 *    relaunch clears it, because iOS suspends a "closed" app rather than
 *    killing it, and the next cold start wedges on the same read anyway.
 *
 * Sized like the bootstrap's read deadline (`AuthContext`) and the query
 * persister's `RESTORE_TIMEOUT_MS`, and for the same reason: this is local
 * storage hardware, not a round trip, so a call that has not answered in
 * seconds is wedged rather than slow.
 *
 * The deadline bounds the CALLER in every case. Whether it also lets the QUEUE
 * move on depends on what the operation does, and the distinction is
 * load-bearing rather than an optimisation — see `KeystoreOrdering` below.
 */
const KEYSTORE_OP_TIMEOUT_MS = 5000;

/**
 * Whether the queue may start the next operation before this one's native call
 * has physically finished.
 *
 * A Capacitor plugin call cannot be cancelled: losing a `Promise.race` abandons
 * our *handle* on it, and the native side goes on to do the thing anyway,
 * whenever it gets there. So the two kinds are not symmetric.
 *
 * - `read` — a `get`. It has no effect to order against, so abandoning it is
 *   free and the queue moves on. That is the whole fix: the auth bootstrap's
 *   read used to sit at the head of this queue forever, and the
 *   `setRefreshToken` a subsequent login performs was stuck behind it.
 * - `mutation` — a `set` or a `remove`. The queue must WAIT for the real call
 *   even after the caller has given up, because "the last operation submitted
 *   is the one whose effect survives" is a correctness property and there is no
 *   way to recall a native write. Letting the chain advance here reopens the
 *   exact race `opChain` exists to prevent, in both directions: an abandoned
 *   `set` landing after a sign-out's `remove` leaves a live refresh token in the
 *   Keychain (which outlives the process, so the next launch silently resumes
 *   the session), and an abandoned `remove` landing after a re-login's `set`
 *   deletes the token that login just wrote.
 *
 * The caller is bounded either way, so a wedged mutation still fails its own
 * caller in five seconds — it just does not let the next mutation overtake it.
 */
type KeystoreOrdering = 'read' | 'mutation';

/** Reject with a transient `SecureTokenStoreError` if `operation` outlives the deadline. */
function withKeystoreDeadline<T>(operation: Promise<T>): Promise<T> {
  // The timer is cleared on the winning path, like the query persister's own
  // `withTimeout`: a race leaves the loser running, and a five-second handle
  // left armed on every keystore call keeps a jsdom worker alive past the test
  // that created it.
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new SecureTokenStoreError('The secure token store did not answer')),
      KEYSTORE_OP_TIMEOUT_MS,
    );
  });
  return Promise.race([operation, deadline]).finally(() => clearTimeout(timer!));
}

function enqueue<T>(operation: () => Promise<T>, ordering: KeystoreOrdering): Promise<T> {
  // The real native call, captured so the chain can wait for it even when the
  // caller's own promise has already been cut short by the deadline.
  let inFlight: Promise<unknown> = Promise.resolve();
  const start = (): Promise<T> => {
    const real = operation();
    inFlight = real.catch(() => undefined);
    return withKeystoreDeadline(real);
  };
  const result = opChain.then(start, start);
  // Keep the chain alive whatever this operation does; each caller sees its own
  // rejection through `result`.
  opChain =
    ordering === 'mutation'
      ? result.catch(() => undefined).then(() => inFlight)
      : result.catch(() => undefined);
  return result;
}

type SecureStoragePluginShape = {
  get: (key: string, convertDate?: boolean, sync?: boolean) => Promise<unknown>;
  set: (
    key: string,
    data: unknown,
    convertDate?: boolean,
    sync?: boolean,
    access?: number
  ) => Promise<void>;
  remove: (key: string, sync?: boolean) => Promise<boolean>;
};

let pluginPromise: Promise<SecureStoragePluginShape> | null = null;

/**
 * Loaded lazily so the web bundle never pulls in native-only code, matching
 * `pushNotifications.ts` and `analytics/posthog.ts`. Memoised because the auth
 * bootstrap and a request interceptor can both reach the store on the same tick.
 */
async function getPlugin(): Promise<SecureStoragePluginShape> {
  if (!pluginPromise) {
    pluginPromise = import('@aparajita/capacitor-secure-storage')
      .then((mod) => mod.SecureStorage as unknown as SecureStoragePluginShape)
      .catch((error) => {
        pluginPromise = null;
        throw new SecureTokenStoreError('Secure storage plugin unavailable', error);
      });
  }
  return pluginPromise;
}

/**
 * Move a token written by a PREVIOUS version of the app out of localStorage.
 *
 * Runs once per process, before the first native read. Without it every user
 * who upgrades is signed out on first launch — the token they have is in the
 * place we stopped looking. The localStorage copy is deleted in the same pass,
 * which is what makes "no refresh token in localStorage on native" true rather
 * than aspirational.
 */
function migrateFromLocalStorage(plugin: SecureStoragePluginShape): Promise<void> {
  if (!migrationPromise) {
    migrationPromise = (async () => {
      const legacy = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
      if (!legacy) return;
      await plugin.set(AUTH_STORAGE_KEYS.refreshToken, legacy);
      localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
    })().catch((error) => {
      // A failed migration must be retryable, not remembered as done.
      migrationPromise = null;
      throw error;
    });
  }
  return migrationPromise;
}

/**
 * The stored refresh token, or null if there is none.
 *
 * @throws SecureTokenStoreError when the keystore itself could not be reached —
 *   never conflated with a null result, because the caller treats the two
 *   differently (see the class docstring).
 */
export async function getRefreshToken(): Promise<string | null> {
  if (!isSecureTokenStoreActive()) {
    return localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
  }
  if (clearedLatch) return null;
  if (readStoredBindingMode() === 'bound') {
    // The stored item needs a live authentication, which this call cannot
    // perform. Either the app is unlocked and `unlockRefreshToken()` has already
    // put the token here, or it is locked and the honest answer is "the store
    // could not be reached" — a TRANSIENT failure, never a null. A null would
    // tell `tokenRefresh` the session is over and sign the person out for having
    // locked their phone.
    if (sessionToken !== null) return sessionToken;
    throw new SecureTokenStoreError('Secure token store is locked');
  }
  const plugin = await getPlugin();
  return enqueue(async () => {
    // Re-checked inside the queue: a clear submitted before this read must win.
    if (clearedLatch) return null;
    try {
      await migrateFromLocalStorage(plugin);
      const value = await plugin.get(AUTH_STORAGE_KEYS.refreshToken);
      return typeof value === 'string' && value.length > 0 ? value : null;
    } catch (error) {
      if (error instanceof SecureTokenStoreError) throw error;
      throw new SecureTokenStoreError('Could not read the secure token store', error);
    }
  }, 'read');
}

/**
 * Persist a freshly minted refresh token, lowering the cleared latch.
 *
 * Also removes any localStorage copy on native: a rotation that ran before the
 * migration, or a code path that wrote the old way, must not leave a stale
 * token behind in the readable store.
 */
export async function setRefreshToken(token: string): Promise<void> {
  clearedLatch = false;
  if (!isSecureTokenStoreActive()) {
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, token);
    return;
  }
  // Whatever was in localStorage is superseded by this write, so the migration
  // is moot — resolve it rather than leaving a later read to run it and
  // overwrite this token with the stale one.
  migrationPromise = Promise.resolve();
  // Captured BEFORE the first await, not after: arming the canary is a native
  // round trip, and a lock that engages during it must still invalidate the hold
  // this write is about to take.
  const epoch = holdEpoch;
  // Decided ONCE, here, and recorded — the read path must not re-derive it from
  // a policy that can change while this item stays as it was written. Arming is
  // what makes the decision real, so the answer is the ARM's and not the
  // policy's: a device with no biometry enrolled has no set to bind to.
  const bind = shouldBindRefreshToken()
    ? (await armBiometrySetGuard()) === 'armed'
    : false;
  const plugin = await getPlugin();
  return enqueue(async () => {
    try {
      await plugin.set(AUTH_STORAGE_KEYS.refreshToken, token);
      localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
      // Only after the write landed: a marker written ahead of a failed write
      // would send every later read down a path with nothing at the end of it.
      writeStoredBindingMode(bind ? 'bound' : 'plain');
      // A rotation must refresh the hold, or every reader for the rest of this
      // unlock would keep spending the token the server has already retired —
      // but only if nothing locked or signed out while the write was in the air.
      if (holdEpoch === epoch) {
        sessionToken = bind ? token : null;
      }
    } catch (error) {
      throw new SecureTokenStoreError('Could not write the secure token store', error);
    }
  }, 'mutation');
}

/**
 * Forget the refresh token.
 *
 * Safe to call from a synchronous handler that is about to reload or navigate:
 * the latch is raised before this function returns, so the token is
 * unreachable from that moment even though the delete is still in flight.
 * Callers that CAN await should, so the delete is durable; the returned promise
 * never rejects, because no sign-out path should fail because a keystore did.
 */
export function clearRefreshToken(): Promise<void> {
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  if (!isSecureTokenStoreActive()) {
    return Promise.resolve();
  }
  clearedLatch = true;
  // Synchronous, for the same reason the latch is: the sign-out paths that
  // cannot await must not leave a readable token behind in memory either.
  sessionToken = null;
  holdEpoch += 1;
  clearStoredBindingMode();
  // Best effort, and deliberately not awaited into the result: a stale canary
  // costs the next sign-in one extra arm() and nothing else, while a sign-out
  // that could fail because a keystore did would be a real defect.
  void disarmBiometrySetGuard();
  return getPlugin()
    .then((plugin) =>
      enqueue(async () => {
        await plugin.remove(AUTH_STORAGE_KEYS.refreshToken);
      }, 'mutation')
    )
    .then(() => undefined)
    .catch(() => undefined);
}

/**
 * How the one authenticated read at unlock went.
 *
 * A discriminated union rather than a thrown error because the caller branches
 * three ways and only one of them is a failure: the difference between "this
 * key is gone forever" and "the device is busy" is the difference between
 * signing somebody out and asking them to try again.
 */
export type UnlockTokenOutcome =
  /** The token is in hand; the app may unlock. */
  | { kind: 'ok' }
  /**
   * The guard is satisfied and there is still nothing to read — the item was
   * removed, or a sign-out landed while this was in flight. Full login.
   */
  | { kind: 'absent' }
  /**
   * The enrolled biometric set has changed since the token was stored. The
   * session on this device is over; the person completes a full login.
   */
  | { kind: 'invalidated' }
  /**
   * The keystore could not answer right now — locked, not yet ready, busy. The
   * session is FINE. Stay locked and let the person try again; do not sign them
   * out over a device that was merely unavailable.
   */
  | { kind: 'unavailable'; cause: unknown };

/**
 * Check the enrolled set and read the token, once, immediately after a
 * successful unlock.
 *
 * The ONLY place a bound keystore item is read. Everything afterwards is served
 * from the in-process hold until the lock engages again, which is what lets the
 * axios interceptor and the auth bootstrap keep refreshing without a prompt —
 * and it is also what makes the guard's answer meaningful, since an enrolment
 * change can only be acted on at a moment when the token is re-read.
 *
 * A no-op that reports `ok` whenever there is no bound item to read — on web
 * and desktop, and on a native build whose token is stored plain. Callers can
 * invoke it unconditionally.
 */
export async function unlockRefreshToken(): Promise<UnlockTokenOutcome> {
  if (!isSecureTokenStoreActive()) return { kind: 'ok' };
  if (readStoredBindingMode() !== 'bound') return { kind: 'ok' };
  if (clearedLatch) return { kind: 'absent' };

  // Before the read, not after: this is the whole point of the bound mode, and a
  // token read into the in-process hold and THEN judged would have been spendable
  // for the length of the judgement.
  const guard = await checkBiometrySetGuard();
  if (guard === 'changed') return { kind: 'invalidated' };
  if (guard === 'unavailable') {
    // A bound marker means the canary WAS armed, so no answer now is a device
    // that cannot answer — stay locked and ask again, never end the session.
    return { kind: 'unavailable', cause: new Error('Biometry set guard unavailable') };
  }

  const epoch = holdEpoch;
  let plugin: SecureStoragePluginShape;
  try {
    plugin = await getPlugin();
  } catch (error) {
    return { kind: 'unavailable', cause: error };
  }

  return enqueue(async (): Promise<UnlockTokenOutcome> => {
    // A sign-out submitted while this was queued must win, exactly as it does
    // for an ordinary read.
    if (clearedLatch) return { kind: 'absent' };
    try {
      const value = await plugin.get(AUTH_STORAGE_KEYS.refreshToken);
      // Re-checked AFTER the read, not only before it: a sign-out that landed
      // while the keystore was answering must win, or this would hand the caller
      // an `ok` — and `AppLockProvider` would unlock the UI — for a session that
      // has already ended.
      if (holdEpoch !== epoch) return { kind: 'absent' };
      if (typeof value === 'string' && value.length > 0) {
        sessionToken = value;
        return { kind: 'ok' };
      }
      return { kind: 'absent' };
    } catch (error) {
      return classifyStoreFailure(error) === 'invalidated'
        ? { kind: 'invalidated' }
        : { kind: 'unavailable', cause: error };
    }
  }, 'read').catch(
    // The callback above maps every failure it can SEE to an outcome, but the
    // deadline in `enqueue` races that callback from the OUTSIDE — so a keystore
    // that stalls past `KEYSTORE_OP_TIMEOUT_MS` rejects around it, past a `catch`
    // that never runs. This function's whole contract is that it answers with a
    // value rather than throwing (see `UnlockTokenOutcome`), and `AppLockProvider`
    // relies on that: an unhandled rejection there leaves the lock screen at
    // `prompting` with no prompt coming, which is this item's own bug class
    // re-created on the unlock path.
    (error): UnlockTokenOutcome => ({ kind: 'unavailable', cause: error }),
  );
}

/**
 * Re-store the token under whichever mode the app lock now calls for.
 *
 * Binding follows the app lock, so BOTH switches move the stored item and
 * neither can wait for the next rotation:
 *
 * - **Lock turned ON** — the existing plain item stays plain, and unbound, until
 *   something rewrites it. Binding that only engaged at the next rotation would
 *   make the setting quietly ineffective for up to an access token's lifetime.
 * - **Lock turned OFF** — worse. The item stays BOUND while the only screen that
 *   could authenticate a read is gone, so the next cold start finds a marker
 *   demanding an unlock that will never be offered, and the person is signed out
 *   for having switched a setting off.
 *
 * A no-op when the stored mode already matches, so it is safe to call on every
 * policy sync. Reads through `getRefreshToken()`, which in bound mode is served
 * from the in-process hold — normally present, because changing the setting means
 * the app is open and unlocked, but NOT guaranteed: `AppLockPolicySync` lives in
 * the authenticated tree, which stays mounted under a `resume` lock. Hence the
 * swallowed read below; do not remove it.
 */
export async function rebindRefreshToken(): Promise<void> {
  if (!isSecureTokenStoreActive()) return;
  if (clearedLatch) return;
  const desired = shouldBindRefreshToken() ? 'bound' : 'plain';
  if (readStoredBindingMode() === desired) return;

  const token = await getRefreshToken().catch(() => null);
  // Nothing to move. A failed read is deliberately swallowed: this is
  // housekeeping on a settings change, and it must never be the thing that ends
  // a session. The next rotation will re-store it correctly anyway.
  if (!token) return;
  await setRefreshToken(token);
}

/**
 * Drop the in-process hold, because the lock has engaged.
 *
 * Synchronous and unconditional. From here every read answers with a transient
 * failure until the next `unlockRefreshToken()`, which is the point: a locked
 * app should not be able to spend the refresh token, and re-reading at the next
 * unlock is also what makes an enrolment change SHOW UP rather than being masked
 * by a token this process read before it happened.
 */
export function lockRefreshToken(): void {
  sessionToken = null;
  holdEpoch += 1;
}

/**
 * Reset module state between tests. Not used by application code.
 */
export function __resetSecureTokenStoreForTests(): void {
  clearedLatch = false;
  migrationPromise = null;
  pluginPromise = null;
  opChain = Promise.resolve();
  sessionToken = null;
  holdEpoch = 0;
}
